import React, { useEffect, useRef } from 'react';
import { Box, Paper } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ChatHeader, ChatInput, ChatMessage, TypingIndicator } from '../components';
import { useChatBot } from '../hooks';
import { Message } from '../types/types';

const welcomeMessages = [
  "Hello! I'm LedgerX365 Assistant. How can I help you with your accounting needs today?",
  "Hi there! Welcome to LedgerX365. I'm here to assist you with any questions about your financial management.",
  "Greetings! I'm your LedgerX365 AI assistant. What would you like to know about your accounting system?",
  "Hello! I'm here to help you navigate LedgerX365. What can I assist you with today?",
  "Welcome to LedgerX365! I'm your AI assistant, ready to help with any accounting or financial questions you have.",
];

export const ChatBotPage: React.FC = () => {
  const theme = useTheme();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  const {
    messages,
    inputMessage,
    setInputMessage,
    isLoading,
    isConnected,
    handleSendMessage,
    handleKeyPress,
  } = useChatBot();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Add welcome message when component mounts
    const randomWelcomeMessage = welcomeMessages[Math.floor(Math.random() * welcomeMessages.length)];
    const welcomeMsg: Message = {
      id: Date.now().toString(),
      text: randomWelcomeMessage,
      sender: 'bot',
      timestamp: new Date(),
    };
    
    // Only add welcome message if no messages exist
    if (messages.length === 0) {
      // This will be handled by the hook, but we can trigger it here
      console.log('Welcome message ready:', welcomeMsg);
    }
  }, []);

  return (
    <Box
      sx={{
        height: 'calc(100vh - 120px)', // Adjust based on your layout
        maxHeight: 'calc(100vh - 120px)',
        width: '50%', // Half width
        maxWidth: '800px', // Maximum width constraint
        minWidth: '400px', // Minimum width constraint
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: theme.palette.background.paper,
        borderRadius: 2,
        overflow: 'hidden',
        boxShadow: 3,
        mx: 'auto', // Center horizontally
        mt: 2,
      }}
    >
      {/* Header */}
      <ChatHeader isConnected={isConnected} />

      {/* Messages Container */}
      <Paper
        elevation={0}
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          backgroundColor: theme.palette.background.paper,
        }}
      >
        {/* Messages List */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              background: theme.palette.background.paper,
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: theme.palette.primary.main,
              borderRadius: '3px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              backgroundColor: theme.palette.primary.dark,
            },
          }}
        >
          {messages.map((message) => (
            <ChatMessage key={message.id} message={message} />
          ))}
          
          {isLoading && <TypingIndicator />}
          
          <div ref={messagesEndRef} />
        </Box>

        {/* Input Area */}
        <ChatInput
          inputMessage={inputMessage}
          setInputMessage={setInputMessage}
          onSendMessage={handleSendMessage}
          onKeyPress={handleKeyPress}
          isConnected={isConnected}
          isLoading={isLoading}
        />
      </Paper>
    </Box>
  );
};
